/**
 * Mẻ nạp ảnh gốc vào đơn (thẻ "Sửa ảnh"), chạy ở tầng APP chứ không ở màn đơn.
 *
 * Trước 02/10/2026 vòng nạp sống trong màn đơn: rời đơn sang trang khác thì máy
 * vẫn gửi nốt nhưng hộp tiến trình biến mất, quay lại đơn cũng không thấy. Mẻ
 * vài trăm tấm chạy hàng chục phút nên CEO chốt (1a·2a·3a 02/10/2026): hộp
 * tiến trình ghim cho cả app, đi trang nào cũng còn, và lỡ đóng tab hay tải lại
 * trang lúc đang nạp thì trình duyệt hỏi lại trước.
 *
 * Nhiều ĐƠN tải song song được (CEO chốt 02/10/2026), mỗi đơn một thẻ tiến
 * trình. Riêng CÙNG một đơn thì mỗi lúc một mẻ: hai mẻ cùng đơn là cùng đếm vào
 * trần ảnh của đơn và thẻ tiến trình nói dối.
 */

import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { create } from "zustand";
import { addOrderPhotos, photoKeys, type PhotoUploadIntent } from "@/features/photo-select/api";
import { naturalCompare } from "@/features/gallery/natural-sort";
import { NETWORK_RETRY_DELAYS_MS, waitOnline } from "@/features/gallery/upload-manager";
import { FormSendError } from "@/lib/api/authed-fetch";
import { reportSlowUpload, reportUploadError } from "@/lib/sentry";
import { recompressJpeg } from "@/lib/jpeg-recompress";
import { uploadEta, uploadSpeed, type UploadSample } from "@/lib/upload-meter";
import { orderKeys } from "./api";

/** Giữ nguyên câu tiếng Việt máy chủ đã viết; chỉ đỡ khi lỗi không có câu nào. */
function errText(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

export type OrderPhotoUploadJob = {
  orderId: string;
  /** Mã đơn để hộp tiến trình nói đang nạp cho đơn nào khi người dùng đã rời đơn. */
  orderCode: string;
  /** "retry" — mạng đứt trước khi tấm này gửi hết, đang chờ để gửi lại. */
  phase: "compress" | "send" | "retry";
  done: number;
  total: number;
  currentName: string;
  uploaded: number;
  failed: number;
  /** Byte/giây trên các tấm vừa gửi; `null` khi chưa tấm nào xong. */
  speed: number | null;
  /** Số giây còn lại ước tính; `null` khi chưa tấm nào xong. */
  eta: number | null;
};

/** Mẻ đang chạy theo mã máy của đơn. */
export const useOrderPhotoUpload = create<{ jobs: Record<string, OrderPhotoUploadJob> }>(() => ({
  jobs: {},
}));

const setJob = (job: OrderPhotoUploadJob) =>
  useOrderPhotoUpload.setState((s) => ({ jobs: { ...s.jobs, [job.orderId]: job } }));

const clearJob = (orderId: string) =>
  useOrderPhotoUpload.setState((s) => {
    const { [orderId]: _done, ...rest } = s.jobs;
    return { jobs: rest };
  });

/** Tên vài tấm đầu cho câu báo; dài quá thì gói phần còn lại thành số. */
function tenAnh(names: string[]): string {
  const shown = names.slice(0, 5).join(", ");
  return names.length > 5 ? `${shown} và ${names.length - 5} ảnh khác` : shown;
}

/**
 * Chọn file → gửi thẳng lên đơn, kèm tiến trình và chặn trần.
 *
 * Trước đây bước này đi hai chặng: tải qua `/notes/upload` lấy URL rồi mới gắn
 * URL vào đơn. Đường đó ghi vào kho ảnh ghi chú/avatar/chứng từ — kho có CDN
 * đọc công khai — nên ảnh buổi chụp nằm sai chỗ và đọc được không cần quyền.
 * Giờ file đi thẳng vào kho ảnh riêng, một chặng.
 *
 * Trả `false` khi không bắt đầu (không có ảnh nào, hoặc đơn này đang có mẻ khác).
 */
export async function runOrderPhotoUpload(opts: {
  orderId: string;
  orderCode: string;
  files: File[];
  qc: QueryClient;
  /** "Đây là ảnh gì?" (QĐ 0312). Vắng = ảnh gốc, như trước. */
  intent?: PhotoUploadIntent;
  /** "Ảnh của ai" (chấm QC bản 3, 4a) — mã thợ chụp, chỉ đơn hai thợ trở lên. */
  shotBy?: string;
}): Promise<boolean> {
  const { orderId, orderCode, qc } = opts;
  if (useOrderPhotoUpload.getState().jobs[orderId]) {
    toast.warning("Đơn này đang tải ảnh — chờ xong mẻ này rồi tải tiếp");
    return false;
  }
  // Thợ hay kéo cả thư mục lẫn file .xmp/.txt đi kèm, và ô chọn tệp cũng lọt
  // tệp lạ khi người dùng đổi bộ lọc — chỉ lấy ảnh, nhưng phải nói rõ đã bỏ
  // bao nhiêu tệp, im lặng là kiểu "mất ảnh" khó truy. Lọc ở đây để cả hai
  // đường (nút + kéo thả) đi chung một cửa.
  // Gửi theo TÊN TỆP máy ảnh (CEO chốt 13/10/2026): máy chủ đánh số thứ tự (seq) theo lượt
  // tải, nên tải đúng IMG_0001, IMG_0002, … là màn iPad / link chọn ảnh xếp theo tên — khách
  // nói "tấm số 5" là thợ mở đúng tấm.
  const files = opts.files
    .filter((f) => f.type.startsWith("image/"))
    .sort((a, b) => naturalCompare(a.name, b.name));
  const skipped = opts.files.length - files.length;
  if (skipped > 0) toast.warning(`Bỏ qua ${skipped} tệp không phải ảnh`);
  if (files.length === 0) return false;

  // Trần ảnh mỗi đơn nằm ở máy chủ (settings `album_photo_limit`, khác nhau
  // theo loại sản phẩm), nên FE KHÔNG giữ một bản số riêng: đơn profile nạp
  // vài trăm tấm mà FE cầm hằng số 60 thì nó chặn oan trước cả khi máy chủ
  // kịp trả lời. Vượt trần thì máy chủ trả 409 kèm đúng con số, và câu đó
  // hiện thẳng cho người dùng ở nhánh catch bên dưới.
  let uploadedTotal = 0;
  let failedTotal = 0;
  // Lượt kiểm 24/10/2026 (L5): trước đây mất mạng một lần ở tấm 2 là bỏ luôn
  // các tấm sau, không thử lại, không nói tấm nào chưa lên.
  let notSent: string[] = [];
  const unsure: string[] = [];
  const samples: UploadSample[] = [];
  let remainingBytes = files.reduce((s, f) => s + f.size, 0);
  let speed: number | null = null;
  let eta: number | null = null;
  const base = { orderId, orderCode, total: files.length };
  // Lúc bắt đầu lần gửi cuối của tấm đang gửi — nhật ký tải chậm tách lúc gửi khỏi lúc chờ gửi lại.
  let lastAttemptAt = 0;
  // Mạng đứt TRƯỚC khi thân ảnh đi hết thì máy chủ chưa nhận ⇒ chờ có mạng rồi
  // gửi lại đúng tấm đó, cộng ~1 phút như hộp tải album.
  const sendWithRetry = async (prepared: File) => {
    for (let attempt = 0; ; attempt += 1) {
      try {
        lastAttemptAt = performance.now();
        return await addOrderPhotos(orderId, [prepared], opts.intent, opts.shotBy);
      } catch (e) {
        if (!(e instanceof FormSendError) || e.bodySent || attempt >= NETWORK_RETRY_DELAYS_MS.length) throw e;
        const job = useOrderPhotoUpload.getState().jobs[orderId];
        if (job) setJob({ ...job, phase: "retry" });
        await waitOnline();
        await new Promise((resolve) => setTimeout(resolve, NETWORK_RETRY_DELAYS_MS[attempt]));
        if (job) setJob({ ...job, phase: "send" });
      }
    }
  };
  try {
    for (const [i, f] of files.entries()) {
      const startedAt = performance.now();
      setJob({
        ...base,
        phase: "compress",
        done: i,
        currentName: f.name,
        uploaded: uploadedTotal,
        failed: failedTotal,
        speed,
        eta,
      });
      // Nén TUẦN TỰ, không Promise.all: mỗi tấm giữ cả ảnh giải mã lẫn bộ đệm
      // mã hoá trong bộ nhớ. Nén xong tấm nào gửi luôn tấm đó để request lên
      // máy chủ không còn phải sống qua cả mẻ vài trăm ảnh.
      const prepared = await recompressJpeg(f);
      setJob({
        ...base,
        phase: "send",
        done: i,
        currentName: prepared.name || f.name,
        uploaded: uploadedTotal,
        failed: failedTotal,
        speed,
        eta,
      });
      const sendAt = performance.now();
      let result: Awaited<ReturnType<typeof addOrderPhotos>>;
      try {
        result = await sendWithRetry(prepared);
      } catch (e) {
        // Mạng đứt khi thân ảnh đã đi hết: máy chủ có thể đã lưu, cửa này không
        // chống trùng ⇒ KHÔNG gửi lại, báo "chưa rõ" rồi đi tiếp tấm sau.
        if (!(e instanceof FormSendError && e.bodySent)) {
          notSent = files.slice(i).map((file) => file.name);
          throw e;
        }
        reportUploadError("order-photos", e);
        unsure.push(f.name);
        remainingBytes -= f.size;
        continue;
      }
      const endAt = performance.now();
      samples.push({
        origBytes: f.size,
        sentBytes: prepared.size,
        sendMs: endAt - sendAt,
        totalMs: endAt - startedAt,
      });
      reportSlowUpload("order-photos", {
        totalMs: endAt - startedAt,
        sendMs: endAt - lastAttemptAt,
        bytes: prepared.size,
      });
      remainingBytes -= f.size;
      speed = uploadSpeed(samples);
      eta = uploadEta(samples, remainingBytes, files.length - (i + 1));
      const uploaded = result.uploaded ?? [];
      const failed = result.failed ?? [];
      uploadedTotal += uploaded.length;
      failedTotal += failed.length;
      setJob({
        ...base,
        phase: "send",
        done: i + 1,
        currentName: prepared.name || f.name,
        uploaded: uploadedTotal,
        failed: failedTotal,
        speed,
        eta,
      });
      for (const item of failed) {
        toast.error(`Ảnh "${item.name}": ${item.reason}`);
      }
      // Ảnh đã lưu nhưng bước tích / đánh dấu theo loại tải không làm được —
      // câu tiếng Việt của máy chủ, hiện nguyên văn.
      for (const w of result.warnings ?? []) {
        toast.warning(w);
      }
    }
    if (uploadedTotal > 0) {
      toast.success(`Đã nạp ${uploadedTotal} ảnh lên đơn`);
    }
  } catch (e) {
    reportUploadError("order-photos", e, { files: files.length, notSent: notSent.length });
    const why = errText(e, "Không nạp được ảnh lên đơn");
    toast.error(
      notSent.length > 0
        ? `${why}. Chưa lên ${notSent.length} ảnh: ${tenAnh(notSent)} — chọn lại các ảnh này để tải tiếp.`
        : why,
    );
  } finally {
    if (unsure.length > 0) {
      toast.warning(
        `Mạng đứt lúc chờ máy chủ trả lời — chưa rõ ${unsure.length} ảnh đã lên chưa: ${tenAnh(unsure)}. Xem trong đơn trước khi tải lại.`,
      );
    }
    if (uploadedTotal > 0 || failedTotal > 0 || unsure.length > 0) {
      void qc.invalidateQueries({ queryKey: photoKeys.orderPhotos(orderId) });
      // Ảnh gốc vào album của đơn ĐANG CHỤP thì máy chủ chuyển đơn sang "Đang
      // sửa ảnh" ngay trong lượt nạp này (photosession signalShootOutput).
      void qc.invalidateQueries({ queryKey: orderKeys.all });
    }
    clearJob(orderId);
  }
  return true;
}
